import { Bot, User, File as FileIcon, Image as ImageIcon, FileText } from 'lucide-react';

import { cn } from '@/lib/utils';

interface ChatBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  showAvatar?: boolean;
  timestamp?: string;
}

interface FileMetadata {
  fileKey: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

/**
 * Try to parse content as file upload metadata
 */
function parseFileContent(content: string): FileMetadata[] | null {
  try {
    const parsed = JSON.parse(content);

    const isValidFile = (obj: unknown): obj is FileMetadata =>
      typeof obj === 'object' &&
      obj !== null &&
      typeof (obj as Record<string, unknown>).url === 'string' &&
      typeof (obj as Record<string, unknown>).fileName === 'string';

    if (Array.isArray(parsed)) {
      const files = parsed.filter(isValidFile);
      return files.length > 0 ? files : null;
    }

    if (isValidFile(parsed)) {
      return [parsed];
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Check if content is a signature URL
 */
function isSignatureUrl(content: string): boolean {
  const trimmed = content.trim();
  return (
    trimmed.startsWith('http') &&
    (trimmed.includes('signature') || trimmed.includes('.png')) &&
    !trimmed.includes(' ')
  );
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Get file icon based on mime type
 */
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <ImageIcon className="size-4" />;
  if (mimeType.includes('pdf')) return <FileText className="size-4" />;
  return <FileIcon className="size-4" />;
}

/**
 * Render message content - handles plain text, file uploads, and signatures
 */
function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  // Check if it's a file upload
  const files = parseFileContent(content);
  if (files) {
    return (
      <div className="p-2 space-y-2">
        {files.map((file, index) => (
          <div key={file.fileKey || index} className="flex items-center gap-3">
            {file.mimeType.startsWith('image/') ? (
              <img
                src={file.url}
                alt={file.fileName}
                className="max-w-[200px] max-h-[150px] rounded-lg object-cover"
              />
            ) : (
              <div
                className={cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2',
                  isUser ? 'bg-primary-foreground/10' : 'bg-muted'
                )}
              >
                {getFileIcon(file.mimeType)}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{file.fileName}</p>
                  <p className={cn('text-xs', isUser ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                    {formatFileSize(file.fileSize)}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
        {files.length === 1 && files[0].mimeType.startsWith('image/') && (
          <p className={cn('px-2 text-sm', isUser ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
            {files[0].fileName}
          </p>
        )}
      </div>
    );
  }

  // Check if it's a signature
  if (isSignatureUrl(content)) {
    return (
      <div className="p-2">
        <div className="rounded-lg bg-white p-2 border border-border/30">
          <img
            src={content.trim()}
            alt="Signature"
            className="max-w-[200px] h-auto"
          />
        </div>
        <p className={cn('mt-1 px-2 text-sm', isUser ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
          Signature
        </p>
      </div>
    );
  }

  // Default: plain text
  return (
    <p className="whitespace-pre-wrap break-words px-4 py-3">{content}</p>
  );
}

/**
 * Individual chat message bubble with avatar and role-based styling.
 * User messages appear on the right, assistant on the left.
 */
export function ChatBubble({
  role,
  content,
  isStreaming,
  showAvatar = true,
  timestamp,
}: ChatBubbleProps) {
  const isUser = role === 'user';

  return (
    <div
      className={cn(
        'flex w-full gap-3',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-300',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      {showAvatar && (
        <div
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full',
            'shadow-sm',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground'
          )}
        >
          {isUser ? (
            <User className="size-4" />
          ) : (
            <Bot className="size-4" />
          )}
        </div>
      )}

      {/* Message bubble */}
      <div
        className={cn(
          'flex max-w-[75%] flex-col gap-1',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-2xl text-[15px] leading-relaxed',
            'shadow-sm',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-md'
              : 'bg-card text-foreground border border-border/50 rounded-tl-md',
            isStreaming && 'animate-pulse'
          )}
        >
          <MessageContent content={content} isUser={isUser} />
        </div>

        {/* Timestamp */}
        {timestamp && (
          <span className="px-1 text-[10px] text-muted-foreground/60">
            {formatTime(timestamp)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Format ISO timestamp to readable time
 */
function formatTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
